import { all, delay, put, select, takeLatest } from 'redux-saga/effects';

import { B2BTeamMember } from '../../modules/b2b/team/types';
import {
  createB2BTeamMemberAction,
  deleteB2BTeamMemberAction,
  fetchB2BTeamAction,
  selectB2BTeamMemberAction,
  updateB2BTeamMemberAction,
} from '../actions/b2bTeam';
import {
  addMember,
  removeMember,
  replaceMember,
  setActiveMember,
  setActiveMemberId,
  setDeletingMemberId,
  setIsLoading,
  setIsLoadingActiveMember,
  setIsSaving,
  setMembers,
} from '../reducers/b2bTeam';
import { b2bTeamMembersSelector } from '../selectors/b2bTeam';
import { safeApply } from './errors';
import { MOCK_FACE_API_TEAM_RESPONSE } from './b2bTeam.fixtures';

const MOCK_FACE_API_DELAY = 300;
const MOCK_ACTIVE_MEMBER_FACE_API_DELAY = 450;
const MOCK_MUTATION_FACE_API_DELAY = 500;

const cloneMember = (member: B2BTeamMember): B2BTeamMember => ({ ...member });

function* fetchB2BTeam() {
  yield put(setIsLoading(true));

  try {
    // Имитируем GET /agent/v1/team через face-api.
    yield delay(MOCK_FACE_API_DELAY);
    yield put(setMembers(MOCK_FACE_API_TEAM_RESPONSE.members.map(cloneMember)));
  } finally {
    yield put(setIsLoading(false));
  }
}

function* selectB2BTeamMember({
  payload,
}: ReturnType<typeof selectB2BTeamMemberAction>) {
  yield put(setActiveMemberId(payload.id));
  yield put(setActiveMember(null));
  yield put(setIsLoadingActiveMember(true));

  try {
    // Имитируем GET /agent/v1/team/:id через face-api.
    yield delay(MOCK_ACTIVE_MEMBER_FACE_API_DELAY);
    const members: B2BTeamMember[] = yield select(b2bTeamMembersSelector);
    const member = members.find(({ id }) => id === payload.id);

    yield put(setActiveMember(member ? cloneMember(member) : null));
  } finally {
    yield put(setIsLoadingActiveMember(false));
  }
}

function* createB2BTeamMember({
  payload,
}: ReturnType<typeof createB2BTeamMemberAction>) {
  yield put(setIsSaving(true));

  try {
    // Имитируем POST /agent/v1/team через face-api.
    yield delay(MOCK_MUTATION_FACE_API_DELAY);
    yield put(
      addMember({
        ...payload.member,
        id: payload.id,
        lastSeen: 'Приглашение отправлено',
        createdAt: new Intl.DateTimeFormat('ru-RU', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(new Date()),
      })
    );
  } finally {
    yield put(setIsSaving(false));
  }
}

function* updateB2BTeamMember({
  payload,
}: ReturnType<typeof updateB2BTeamMemberAction>) {
  yield put(setIsSaving(true));

  try {
    // Имитируем PATCH /agent/v1/team/:id через face-api.
    yield delay(MOCK_MUTATION_FACE_API_DELAY);
    yield put(replaceMember(cloneMember(payload.member)));
  } finally {
    yield put(setIsSaving(false));
  }
}

function* deleteB2BTeamMember({
  payload,
}: ReturnType<typeof deleteB2BTeamMemberAction>) {
  yield put(setDeletingMemberId(payload.id));

  try {
    // Имитируем DELETE /agent/v1/team/:id через face-api.
    yield delay(MOCK_MUTATION_FACE_API_DELAY);
    yield put(removeMember(payload.id));
  } finally {
    yield put(setDeletingMemberId(null));
  }
}

export default function* b2bTeamSaga() {
  yield all([
    takeLatest(fetchB2BTeamAction, safeApply(fetchB2BTeam)),
    takeLatest(selectB2BTeamMemberAction, safeApply(selectB2BTeamMember)),
    takeLatest(createB2BTeamMemberAction, safeApply(createB2BTeamMember)),
    takeLatest(updateB2BTeamMemberAction, safeApply(updateB2BTeamMember)),
    takeLatest(deleteB2BTeamMemberAction, safeApply(deleteB2BTeamMember)),
  ]);
}
